/* Zivol Grove — the only JS this product page adds.
   Vanilla, no dependencies, ES module (so duplicate <script> tags for the same
   URL execute exactly once). Three independent features, each a no-op when its
   markup is absent, so a section can be removed from the template without
   breaking the others.

   Deliberately NOT here: any animation beyond the two the brief allows, any
   observer that keeps firing after it has done its job, any layout read in a
   pointer handler (the swap writes a CSS custom property and lets the
   compositor do the rest). */

const onReady = (fn) =>
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn, { once: true })
    : fn();

/* -- 1. Tier cards -> variant, price, savings ----------------------------- */
function initTiers(root) {
  const variantInput = root.querySelector('[data-grove-variant-id]');
  const moveGallery = showVariantMedia(root);
  const priceOut = root.querySelector('[data-grove-buy-price]');
  const savings = root.querySelector('[data-grove-savings]');
  const button = root.querySelector('[data-grove-buy]');
  if (!variantInput || !priceOut) return;

  const cards = [...root.querySelectorAll('[data-grove-tier]')];
  let lastVariantId = variantInput.value;

  // Sticky bar. It mirrors state and forwards clicks; it owns no cart logic,
  // so there is one purchase path rather than two that can drift apart.
  const sticky = root.querySelector('[data-grove-sticky]');
  const stickyName = root.querySelector('[data-grove-sticky-name]');
  const stickyPrice = root.querySelector('[data-grove-sticky-price]');
  const stickyBuy = root.querySelector('[data-grove-sticky-buy]');
  if (stickyBuy && button) {
    stickyBuy.addEventListener('click', () => button.click());
  }

  const sync = () => {
    const tier = root.querySelector('input[name="grove-tier"]:checked');
    if (!tier) return;
    const card = tier.closest('[data-grove-tier]');

    // A card either carries the variant itself (Set) or delegates to the wood
    // radio inside it (Pair, Single).
    const wood = card && card.querySelector('input[data-variant-id]:checked');
    const source = wood || tier;
    const id = source.getAttribute('data-variant-id');
    if (!id) return;

    variantInput.value = id;
    priceOut.textContent = source.getAttribute('data-price') || '';

    if (savings) {
      // Two nodes now, not one string. The previous version assigned
      // savings.textContent, which would erase the amount/caption spans on the
      // first tier change and leave the line as flat text for the rest of the
      // session - the treatment would survive the server render and die on the
      // first click.
      const amount = source.getAttribute('data-savings') || '';
      const versus = source.getAttribute('data-savings-vs') || '';
      const amountEl = savings.querySelector('[data-grove-savings-amount]');
      const versusEl = savings.querySelector('[data-grove-savings-vs]');
      if (amountEl) amountEl.textContent = amount;
      if (versusEl) versusEl.textContent = versus;
      savings.hidden = amount === '';
    }

    /* Composition follows the chip. Every configuration except the two singles
       contains lamps of different sizes, so the line has to change with the
       selection — a static "2 lamps" is what let a buyer choose Pair — Walnut
       expecting a matching pair and receive a tall and a small. Only written
       when the selected chip carries one, so a card without compositions keeps
       whatever its Liquid rendered. */
    const composition = card && card.querySelector('[data-grove-composition]');
    const compositionText = source.getAttribute('data-composition');
    if (composition && compositionText) composition.textContent = compositionText;

    /* THE CARD SHOWS ITS OWN LAMPS.

       The gallery updates on selection, but on a phone it is ~1400px above the
       cards — so comparing two tiers meant tap, scroll up, look, scroll back,
       tap, scroll up again. Four gestures for the one interaction that decides
       the purchase, and worse now there are seven options.

       Scrolling the gallery into view is the obvious fix and it is the wrong
       one: it was built on 2026-08-21, measured at 380x820, and removed —
       the page had to move ~420px, which threw the chips off-screen and took
       control away from the buyer mid-comparison. See the note above
       initSwap; do not reintroduce it.

       So the image comes to the buyer instead of the buyer going to the image.
       Every card carries a thumbnail seeded server-side from its own default
       chip, which means the three tiers can be compared with NO taps at all —
       and a chip change updates only its own card, in place, with the page
       perfectly still. */
    const thumb = card && card.querySelector('[data-grove-thumb]');
    const thumbSrc = source.getAttribute('data-image');
    if (thumb && thumbSrc && thumb.getAttribute('src') !== thumbSrc) {
      thumb.setAttribute('src', thumbSrc);
    }

    if (button) {
      const sold = source.getAttribute('data-unavailable') === 'true';
      button.disabled = sold;
    }

    cards.forEach((c) => c.classList.toggle('is-selected', c === card));

    if (stickyPrice) stickyPrice.textContent = source.getAttribute('data-price') || '';
    if (stickyName && card) {
      const tierName = card.querySelector('.grove-tier__name');
      const woodLabel = wood && wood.closest('label');
      const parts = [tierName ? tierName.textContent.trim() : ''];
      if (woodLabel) parts.push(woodLabel.textContent.replace(/\s+/g, ' ').trim());
      stickyName.textContent = parts.filter(Boolean).join(' · ');
    }
    if (stickyBuy) {
      const sold = source.getAttribute('data-unavailable') === 'true';
      stickyBuy.disabled = sold;
      stickyBuy.textContent = sold ? 'Sold out' : 'Add to cart';
    }

    // Only ask for a re-render when the variant actually changed, so repeat
    // clicks on the selected card do not spam the section-render endpoint.
    if (id !== lastVariantId) {
      lastVariantId = id;
      if (moveGallery) moveGallery(id);
    }
  };

  // Choosing a wood inside a card also selects that card — otherwise the click
  // silently changes nothing the buyer can see.
  // Reveal only once the real Add to Cart has left the viewport. An
  // IntersectionObserver rather than a scroll handler: no work on every frame,
  // and it stays correct if the button moves for any reason.
  if (sticky && button && 'IntersectionObserver' in window) {
    const show = (visible) => {
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (stickyBuy) stickyBuy.tabIndex = visible ? 0 : -1;
    };
    new IntersectionObserver(
      (entries) => entries.forEach((e) => show(!e.isIntersecting && e.boundingClientRect.top < 0)),
      { threshold: 0 }
    ).observe(button);
  }

  root.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.name && input.name.startsWith('grove-wood')) {
      const card = input.closest('[data-grove-tier]');
      const tier = card && card.querySelector('input[name="grove-tier"]');
      if (tier) tier.checked = true;
    }
    sync();
  });

  sync();
}


/* -- 1b. Tier -> gallery. Client-side only ---------------------------------
   WHAT WAS HERE BEFORE, and why it changed. The previous version fetched a full
   section render (`?variant=…&section_id=…`) and handed it to the theme via
   StandardEvents.productSelect, which made media-gallery.js REPLACE itself with
   a freshly rendered gallery. Measured, that did work — the variant's media was
   hoisted to slide 0 and the scroll reset — but it cost a network round trip per
   chip click, threw away and rebuilt the gallery element, and reset the buyer's
   position in the strip as a side effect rather than a decision.

   This does the same job with no request and no re-render: look up the selected
   variant's media id, find the slide already carrying it, and drive the theme's
   own `select(index)` — the exact method the thumbnail strip calls via
   `on:click="/select/N"`. Every frame is already in the DOM, so there is nothing
   to fetch.

   The caller guards on `id !== lastVariantId`, so this runs on a real variant
   change and not on unrelated re-renders or repeat clicks on the current chip. */
function showVariantMedia(root) {
  const section = root.closest('.shopify-section');
  if (!section) return;

  const slideshow = section.querySelector('slideshow-component');
  const map = section.querySelector('[data-grove-variant-media]');
  if (!slideshow || typeof slideshow.select !== 'function' || !map) return;

  let lookup;
  try {
    lookup = JSON.parse(map.textContent || '{}');
  } catch {
    return;
  }
  return (variantId) => {
    const mediaId = lookup[String(variantId)];
    if (!mediaId) return; // variant has no image of its own — leave the gallery be

    const slides = [...section.querySelectorAll('slideshow-slide')];
    const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));
    if (index < 0) return;

    slideshow.select(index);
  };
}

/* NO SCROLL-INTO-VIEW ON VARIANT CHANGE — deliberately removed 2026-08-21.

   A version of this brought the gallery back into view on mobile after a chip
   change. It worked, but measured at 380x820 the page had to move ~420px and
   the chips went off-screen — the gallery is ~410px tall and the chips sit
   ~1400px down, so the two genuinely cannot share a screen. That takes the
   control away from the buyer at the moment they are using it: anyone
   comparing two finishes has to scroll back down for every comparison.

   The owner's call, and the right one: not seeing the change immediately is
   the lesser cost. The real fix is the gallery's POSITION relative to the buy
   column on mobile, which is a layout question, not a scroll one. It stays
   queued (raised 2026-08-16). Do not reintroduce a scroll here. */

/* -- 2. Lightswap before/after ------------------------------------------- */
function initSwap(swap) {
  const range = swap.querySelector('[data-grove-range]');
  if (!range) return;

  // If the visitor asked for less motion the CSS has already laid the two
  // images out side by side and removed the control; don't bind anything.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const paint = (value) => swap.style.setProperty('--grove-pos', value + '%');
  paint(range.value);
  range.addEventListener('input', () => paint(range.value));

  // Pointer events cover mouse, touch and pen in one path — no separate
  // touchmove handler, and no mouse-only assumption.
  let dragging = false;
  const fromEvent = (event) => {
    const rect = swap.getBoundingClientRect();
    if (!rect.width) return;
    const pct = ((event.clientX - rect.left) / rect.width) * 100;
    const clamped = Math.max(0, Math.min(100, pct));
    range.value = String(clamped);
    paint(clamped);
  };

  swap.addEventListener('pointerdown', (event) => {
    dragging = true;
    swap.setPointerCapture(event.pointerId);
    fromEvent(event);
  });
  swap.addEventListener('pointermove', (event) => {
    if (dragging) fromEvent(event);
  });
  const stop = (event) => {
    if (!dragging) return;
    dragging = false;
    if (swap.hasPointerCapture(event.pointerId)) swap.releasePointerCapture(event.pointerId);
  };
  swap.addEventListener('pointerup', stop);
  swap.addEventListener('pointercancel', stop);
}


/* -- 3. Tap acknowledgement -------------------------------------------------
   Measured on a real device: the cart drawer opens 2037ms after the tap, and
   its 260ms slide runs correctly once it does. The defect was never the
   animation — it was two seconds of total silence, with the button showing no
   state at all, which invites a second tap and a double add.

   aria-busy is the acknowledgement: it is real assistive-tech state, not
   decoration, and CSS dims from it. No spinner, no text swap. Cleared on the
   theme's own cartLinesUpdate, so it resolves exactly when the drawer has the
   cart — never on a timer that could lie. */
function initPending(root) {
  const button = root.querySelector('[data-grove-buy]');
  const form = root.querySelector('form[action*="/cart/add"]');
  if (!button || !form) return;

  const clear = () => button.removeAttribute('aria-busy');
  form.addEventListener('submit', () => button.setAttribute('aria-busy', 'true'));

  import('@shopify/events')
    .then(({ StandardEvents }) => {
      document.addEventListener(StandardEvents.cartLinesUpdate, clear);
      document.addEventListener(StandardEvents.cartError, clear);
    })
    .catch(() => {
      // No importmap: fall back to clearing when the form settles, so the
      // button can never be left stuck in a pending state.
      form.addEventListener('submit', () => setTimeout(clear, 6000));
    });
}


/* -- 4. Deferred below-fold images -----------------------------------------
   Measured on Slow 4G: 268 KB of imagery the buyer has not scrolled to — the
   two Lightswap frames (159.9 KB) and the three story rows (107.8 KB) — was
   downloading INSIDE the 2956ms LCP window, against 26 KB for the hero that
   actually defines LCP. loading="lazy" does not prevent that: Chrome's lazy
   threshold widens on slow connections, which is exactly when it hurts.

   So the URLs sit in data-src/data-srcset and are attached here, 300px before
   the image enters the viewport — far enough that a scrolling buyer never sees
   a gap, late enough that the hero is not competing with them.

   NOTHING ABOVE THE FOLD IS DEFERRED. Only these two sections carry
   data-grove-defer; the gallery is the theme's own and is untouched.
   width/height stay on every element, so reserving space does not depend on
   this running and CLS stays 0. */
function initDeferredImages() {
  const images = [...document.querySelectorAll('img[data-grove-defer]')];
  if (!images.length) return;

  const attach = (img) => {
    const srcset = img.getAttribute('data-srcset');
    const src = img.getAttribute('data-src');
    if (srcset) img.srcset = srcset;
    if (src) img.src = src;
    img.removeAttribute('data-grove-defer');
  };

  if (!('IntersectionObserver' in window)) {
    images.forEach(attach);
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        attach(entry.target);
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '300px 0px' }
  );
  images.forEach((img) => io.observe(img));
}


/* -- 5. Gallery video: play it when it is actually LOOKED at ---------------
   The gallery video autoplays (product-media.liquid hardcodes video_autoplay)
   and does not loop, because it is not a seamless loop: it opens in daylight
   and closes at night, so looping would hard-cut from night back to day every
   five seconds and read as broken.

   Those two facts together produced a third: the video started on page load
   while sitting off-screen at slide 4, ran its five seconds, and ENDED. By the
   time a buyer swiped to it, it was a frozen final frame. Measured before this
   existed: currentTime 5.00, ended true, before the slide had ever been shown.
   The whole point of the asset is the transition, and nobody was seeing it.

   So: rewind and play when its slide comes into view, pause when it leaves.
   Revisiting the slide plays it again, which is what someone who swipes back
   is asking for. Additive only — it sets currentTime and calls play/pause, and
   leaves the theme's own mute/lightbox handling alone.
*/
function initGalleryVideo() {
  const videos = [...document.querySelectorAll('.grove-hero__gallery video')];
  if (!videos.length || !('IntersectionObserver' in window)) return;

  /* A CUE THAT THIS IS FOOTAGE, NOT A PHOTOGRAPH.

     The theme renders gallery video with `disable_controls: true` and no
     poster button — that markup exists in product-media.liquid only for 3D
     models. So a video slide is a bare <video> with no controls, no duration
     and no play glyph. This clip opens on a static shelf and holds it for two
     seconds before anything moves, which means for those two seconds it is
     indistinguishable from a slow-loading still. A buyer swipes past it.

     Injected from JS rather than added to the shared snippet, because the
     snippet renders every product's gallery and this is a Grove problem.
     Shown while the video is not playing, hidden while it is — a persistent
     play button would be wrong on something that plays itself. */
  /* NEUTRALISE THE RAW autoplay ATTRIBUTE.

     product-media.liquid hardcodes video_autoplay:true, so the served tag
     carries autoplay="autoplay" and the browser starts playback at PARSE time —
     before this module exists. That is the original fault: the clip ran its five
     seconds off-screen at slide 4 and a buyer arrived at a frozen last frame.

     Adding the observer did not remove that path, it only added a second one.
     Both were live at once. Strip the attribute and reset, so playback has
     exactly ONE owner: the IntersectionObserver below. A video already on screen
     is picked up immediately by the observer's first callback, so nothing that
     should be playing stays paused. */
  videos.forEach((v) => {
    v.removeAttribute('autoplay');
    v.autoplay = false;
    if (!v.paused) v.pause();
    try { v.currentTime = 0; } catch (e) { /* not seekable yet */ }
  });

  const fmt = (secs) => {
    const t = Math.round(secs || 0);
    return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0');
  };
  videos.forEach((v) => {
    const host = v.parentElement;
    if (!host || host.querySelector('.grove-video-cue')) return;
    const cue = document.createElement('span');
    cue.className = 'grove-video-cue';
    cue.setAttribute('aria-hidden', 'true');
    const paint = () => {
      cue.textContent = '▶ ' + fmt(isFinite(v.duration) && v.duration ? v.duration : 5);
    };
    paint();
    v.addEventListener('loadedmetadata', paint);
    const show = () => cue.classList.remove('is-playing');
    const hide = () => cue.classList.add('is-playing');
    v.addEventListener('playing', hide);
    v.addEventListener('pause', show);
    v.addEventListener('ended', show);
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(cue);
  });

  /* ROOT IS THE CAROUSEL, NOT THE VIEWPORT.

     First version observed against the viewport at threshold 0.6 and had the
     same fault it was written to fix: in a horizontal carousel every slide sits
     inside the strip, so slide 4 counted as intersecting the moment the gallery
     was on screen. The observer started the video on load, it ran its five
     seconds off-screen, and arriving at the slide showed a frozen last frame —
     measured currentTime 5.00, ended true, without the slide ever being shown.

     Rooting on the scroller measures the slide against the CAROUSEL's visible
     box instead of the page's, so "in view" means the buyer is actually looking
     at it. threshold 0.9 because slides sit edge to edge: a lower value fires
     while the previous frame is still half on screen. */
  const scroller =
    videos[0].closest('slideshow-component, [class*="slideshow"]') ||
    videos[0].closest('.grove-hero__gallery');

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        if (entry.isIntersecting) {
          // Muted + playsinline is what makes this allowed without a gesture.
          v.muted = true;
          try {
            v.currentTime = 0;
          } catch (e) {
            /* not seekable yet; play() below still starts it from wherever it is */
          }
          const p = v.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    },
    { root: scroller || null, threshold: 0.9 }
  );
  videos.forEach((v) => io.observe(v));
}

onReady(() => {
  document.querySelectorAll('[data-grove-hero]').forEach((root) => {
    initTiers(root);
    initPending(root);
  });
  document.querySelectorAll('[data-grove-swap]').forEach(initSwap);
  initDeferredImages();
  initGalleryVideo();
});


/* THE ARC ADD-ON.
   The add is the theme's own <product-form-component>, so there is no fetch
   here and no drawer handling: submitting that form gives the same cart
   update, drawer open and error states as every other product on the store.

   An earlier version added the tray AND the selected lamp from a hand-rolled
   fetch, then opened the drawer by clicking the LAMP's Add to Cart. That put a
   $145 Set into the basket of anyone who asked for a $29 tray, and a second
   Set into the basket of anyone who had already added one. The condition the
   copy states is carried by the copy. It is never enforced by adding something
   the buyer did not ask for.

   What is left is keeping the posted variant and the photograph in step with
   the chosen finish. */
function syncGroveAddon(input) {
  const row = input.closest('.grove-addon');
  if (!row) return;

  // The variant the form posts. Without this the form keeps whichever id was
  // rendered first, and a buyer who picks red is sold black.
  const idField = row.querySelector('[data-grove-addon-variant]');
  if (idField && input.value) idField.value = input.value;

  // The add-on product has a single photograph; each finish borrows the real
  // Arc product's shot of that colour. A finish with no matching image keeps
  // the picture it has rather than blanking the slot.
  const thumb = row.querySelector('[data-grove-addon-thumb]');
  const src = input.dataset.finishImg;
  if (thumb && src) {
    thumb.src = src;
    thumb.alt = input.dataset.finishAlt || '';
  }
}

// Delegated from document: the buy column re-renders on tier change.
document.addEventListener('change', (e) => {
  const input = e.target.closest('input[name="grove-addon-finish"]');
  if (input) syncGroveAddon(input);
});

// THE POSTED VARIANT IS RESOLVED AT SUBMIT, NOT KEPT IN THE INPUT.
// The buy column re-renders when the lamp tier changes. A DOM morph can keep a
// radio's checked state (a live property) while restoring the hidden input's
// value from the server-rendered attribute - which is always the FIRST finish.
// The picker would then read "Oxblood Red" while the form posted Matte Black,
// and the buyer receives the wrong tray with no way to notice. Reading the
// checked radio in the capture phase, immediately before product-form-component
// handles the submit, makes the posted variant always equal the visible one no
// matter what happened to the DOM in between.
document.addEventListener(
  'submit',
  (e) => {
    const form = e.target;
    if (!form || typeof form.querySelector !== 'function') return;
    const idField = form.querySelector('[data-grove-addon-variant]');
    if (!idField) return;
    const picked = document.querySelector('input[name="grove-addon-finish"]:checked');
    if (picked && picked.value) idField.value = picked.value;
  },
  true
);

// Apply whichever finish is checked at load, so the picture and the posted
// variant agree with the picker before anyone touches it.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('input[name="grove-addon-finish"]:checked').forEach(syncGroveAddon);
});
